import { useRef, useState, useEffect, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 16 — Salsa Piano Claves (Son · Rumba · Bossa).
 *
 * Three two-bar Afro-Cuban patterns. Each is shown as a 16-cell strip
 * (2 bars × 8 eighths) with proper beat-position labels (1 & 2 & 3 & 4 &).
 * Slow/fast pulse toggle controls LH (slow = beats 1 & 3 only; fast =
 * every beat). Tempo slider 60–130 BPM. The cells are sized so the
 * structural difference between Son and Rumba (one eighth shifted) is
 * visible at a glance.
 */

interface ClavePattern {
  id: string;
  name: string;
  description: string;
  /** 16-cell pattern (2 bars × 8 eighths). True = strike. 3:2 form. */
  cells_3_2: boolean[];
}

const CLAVES: ClavePattern[] = [
  {
    id: "son",
    name: "Son",
    description: "The mother clave. Cuban son, mambo, salsa. Bar 1 = 1 · & of 2 · 4. Bar 2 = 2 · 3.",
    cells_3_2: [
      true, false, false, true, false, false, true, false,  // bar 1: 1, & of 2, 4
      false, false, true, false, true, false, false, false, // bar 2: 2, 3
    ],
  },
  {
    id: "rumba",
    name: "Rumba",
    description: "Like Son, but bar 1's third hit shifts from beat 4 to the AND of 4. Jason finds it more exciting.",
    cells_3_2: [
      true, false, false, true, false, false, false, true,  // bar 1: 1, & of 2, & of 4
      false, false, true, false, true, false, false, false, // bar 2: same as Son
    ],
  },
  {
    id: "bossa",
    name: "Bossa Nova",
    description: "Bar 1 = tresillo (same as Son: 1, & of 2, 4). Bar 2: 2 and the &-of-3. Jason also calls it the EDM clave — his example is Coldplay's A Sky Full of Stars.",
    cells_3_2: [
      true, false, false, true, false, false, true, false,  // bar 1: 1, & of 2, 4 (tresillo — same as Son)
      false, false, true, false, false, true, false, false, // bar 2: 2, &-of-3
    ],
  },
];

type Pulse = "slow" | "fast";

export default function PianoIntermediate16() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "16");
  return (
    <LessonShell course={course} courseSlug="piano-intermediate" lessonOrder={lesson.order} title={lesson.title} description={lesson.desc} videoUrl={lesson.video} thumbUrl={lesson.thumb} accent={accent}>
      <AnimatedExplainer
        src="/animations/piano-intermediate/PI21-Salsa-Claves-Animated.mp4"
        caption={`Son · Rumba · Bossa — three 5-stroke claves across a 2-bar frame, side-by-side`}
        hint={`≈ 24 sec · the skeleton of Cuban music`}
      />
      <KeyProvider defaultTonic="C" availableQualities={["natural-minor"]}>
        <PIShell title="Salsa Piano — Claves (Son · Rumba · Bossa)" exerciseGoal="Three two-bar Afro-Cuban patterns over a root–fifth pulse. Rumba and Bossa each move one stroke of the Son clave by a single eighth — and that one note changes the feel." bpm={104} difficulty="Intermediate">
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [activeId, setActiveId] = useState<string>("son");
  const [flip2_3, setFlip2_3] = useState(false);
  const [pulse, setPulse] = useState<Pulse>("slow");
  const [bpm, setBpm] = useState(104);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);

  const clave = CLAVES.find((c) => c.id === activeId)!;
  const cells = flip2_3 ? [...clave.cells_3_2.slice(8), ...clave.cells_3_2.slice(0, 8)] : clave.cells_3_2;

  const stop = () => { handleRef.current?.stop(); handleRef.current = null; setRunning(false); setStep(-1); };

  // Live tempo — ramp the Transport BPM whenever the slider moves during playback
  useEffect(() => {
    if (running) setRhythmBpm(bpm, 0.05);
  }, [bpm, running]);

  const play = async () => {
    kickAudio();
    stop();
    setRunning(true);
    // 16 cells × eighth-note resolution (subdivide=2) = 16 eighths = 2 bars in 4/4
    const totalSteps = 16;
    const events: { beat: number; notes: string[]; durationSteps: number; velocity?: number }[] = [];
    // Jason's chart: C minor voiced G-C-E♭, then "maybe add an F major" voiced A-C-F.
    const chords = [[-5, 0, 3], [-3, 0, 5]].map((v) => v.map((o) => tonicMidi + o));
    const bassRoot = tonicMidi - 12;
    // "You can either do C to the G or C to the lower G. I like the lower one."
    const bassFifth = bassRoot - 5;
    cells.forEach((hit, i) => {
      if (hit) events.push({
        beat: i,
        notes: chords[i < 8 ? 0 : 1].map((m) => midiToPitchName(m, k.useFlats)),
        durationSteps: 1,
        velocity: 0.9,
      });
    });

    // LH pulse — slow (beats 1, 3) or fast (every beat)
    const pulsePositions = pulse === "slow" ? [0, 4, 8, 12] : [0, 2, 4, 6, 8, 10, 12, 14];
    pulsePositions.forEach((pos, n) => {
      events.push({
        beat: pos,
        notes: [midiToPitchName(n % 2 === 0 ? bassRoot : bassFifth, k.useFlats)],
        durationSteps: pulse === "slow" ? 4 : 2,
        velocity: 0.7,
      });
    });

    handleRef.current = await playRhythm(events, {
      bpm, subdivide: 2, totalSteps, loop: true, click: true, beatsPerBar: 4,
      onStep: (s) => setStep(s),
    });
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram highlights={[
          { pc: k.tonicPc, tone: "root", label: "1" },
          { pc: ((k.tonicPc + 7) % 12 + 12) % 12, tone: "voice", label: "5" },
        ]} octaves={2} startOctave={3} playable />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          The clave is a rhythm, not a pitch. Jason plays it as chord hits — C minor, then F — over
          a left hand that toggles the root with the lower fifth, like a bass player.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Three sticks, three cultures">
          <p>
            <strong>Clave</strong> means "stick" in Spanish — these patterns started as two wooden
            sticks struck together. Each clave is a two-bar phrase. The first bar has THREE hits;
            the second bar has TWO. That's the <strong>3:2 clave</strong>. Reverse the bars and
            you get the <strong>2:3 clave</strong> — same notes, different launch.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "There are three claves which I generally share with students and use myself in
            compositions and which I find in pop music."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The comparator" title="See all three side by side">
          <ClaveComparator
            claves={CLAVES}
            activeId={activeId}
            flip2_3={flip2_3}
            step={running ? step : -1}
            onPick={(id) => { stop(); setActiveId(id); }}
          />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button onClick={play} disabled={running}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50">
              ▶ Loop the {clave.name} clave
            </button>
            {running && <button onClick={stop} className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20">■ Stop</button>}

            <label className="flex select-none items-center gap-1.5 text-[12px] text-cyan-200">
              <input type="checkbox" checked={flip2_3} onChange={(e) => { stop(); setFlip2_3(e.target.checked); }} className="h-3.5 w-3.5 accent-amber-400" />
              Flip to 2:3
            </label>

            <div className="ml-2 inline-flex items-center gap-1 rounded-lg border border-cyan-400/30 bg-cyan-500/5 p-0.5">
              {(["slow", "fast"] as Pulse[]).map((p) => (
                <button key={p} onClick={() => { stop(); setPulse(p); }}
                  className={`rounded-md px-3 py-1 font-mono text-[11px] font-bold uppercase ${
                    pulse === p ? "bg-amber-400 text-zinc-900" : "text-cyan-200 hover:bg-cyan-500/15"
                  }`}>
                  {p} pulse
                </button>
              ))}
            </div>

            <label className="flex items-center gap-2 text-[12px] text-cyan-200">
              <span className="font-mono uppercase tracking-wider text-cyan-300/80">Tempo</span>
              <input type="range" min={60} max={130} step={1} value={bpm} onChange={(e) => setBpm(Number(e.target.value))} className="h-1 w-32 accent-amber-400" />
              <span className="w-10 font-mono text-[11px] text-cyan-100">{bpm}</span>
            </label>
          </div>

          <p className="mt-3 text-[14px] text-cyan-50/95">
            <strong>{clave.name}</strong> · <em>{clave.description}</em>
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The Son clave" title="Origin of the salsa universe">
          <p>
            The <strong>Son clave</strong> is the foundational rhythm of Cuban son, mambo and
            salsa. The 3-side hits "1 · & of 2 · 4". The 2-side hits "2 · 3". Three notes leaning
            forward in bar 1, two notes resolving in bar 2.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="The Rumba clave" title="One eighth more 'off'">
          <p>
            The <strong>Rumba clave</strong> is identical to Son except the third hit of bar 1
            shifts from beat 4 to the <em>AND of 4</em>. That single eighth more of syncopation
            pushes against the downbeat instead of landing on it.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "I feel the rumba is a lot more exciting than the son clave because of that off beat
            variation."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="The Bossa clave" title="Bar 1 stays, bar 2 shifts">
          <p>
            <strong>Bossa Nova</strong> keeps bar 1 exactly as Son (the tresillo: 1 · &-of-2 · 4).
            The change is in bar 2: the second hit slides from beat{" "}
            <strong>3</strong> to the <strong>and of 3</strong> — one eighth later, the same
            size of nudge that turned Son into Rumba, just applied to a different stroke.
            Count the gaps between strokes and the family falls out cleanly: Son is
            3-3-4-2-4 eighths, Rumba 3-4-3-2-4, Bossa 3-3-4-3-3. That single displacement
            makes bar 2 feel later. Jason also calls it the EDM clave, because it's used a lot in
            electronic dance music — his example is Coldplay's <em>A Sky Full of Stars</em>.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Body before keyboard" title="Clave in the right, pulse in the left">
          <p>
            On a bongo or on your body: tap the clave with the right hand and the pulse with the
            left. When both hands feel locked, transfer to the piano — the right hand takes the
            clave hits, the left hand keeps the pulse.
          </p>
          <PIInsight>
            <p>
              Slower is better for the pulse: rather than all four quarters, Jason alternates —
              beats 1 and 3. That's what creates the dance vibe. And count the in-between eighths
              too, even though the clave doesn't hit them.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Play along" title="Loop with chord stabs and root-fifth bass">
          <LessonMidi courseSlug="piano-intermediate" order="16" label={`${clave.name} clave · ${k.tonicName}`} originalBpm={bpm} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="8" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={<>One clave per rung. Slow pulse first, fast pulse second. By the fourth rung your ear can name them on the radio.</>}
            rungs={[
              { title: "Son on the body",          minutes: 4, accent: "amber",   detail: "Clave in the right hand, pulse in the left. 3:2, then 2:3. Slow pulse. No piano yet." },
              { title: "Son on the piano",         minutes: 4, accent: "cyan",    detail: "Same clave, now as chord hits in the right hand and root–lower-fifth bass in the left. Slow pulse." },
              { title: "Rumba",                    minutes: 4, accent: "rose",    detail: "Shift bar 1's third hit to the AND of 4. Listen for the extra push. Then flip it to 2:3." },
              { title: "Bossa",                    minutes: 4, accent: "violet",  detail: "Bar 1 stays as tresillo (same as Son); bar 2's second hit moves from beat 3 to the AND of 3. Then play a melody or arpeggios on the clave hits." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: ClaveComparator — large, beat-labeled, scannable ── */
function ClaveComparator({
  claves, activeId, flip2_3, step, onPick,
}: {
  claves: ClavePattern[]; activeId: string; flip2_3: boolean; step: number; onPick: (id: string) => void;
}) {
  const colors: Record<string, { hit: string; ring: string }> = {
    son:   { hit: "bg-amber-400",  ring: "ring-amber-300/60" },
    rumba: { hit: "bg-cyan-400",   ring: "ring-cyan-300/60" },
    bossa: { hit: "bg-rose-400",   ring: "ring-rose-300/60" },
  };
  const labels = ["1", "&", "2", "&", "3", "&", "4", "&", "1", "&", "2", "&", "3", "&", "4", "&"];

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ▥ Clave comparator · 2 bars × 8 eighths
        </div>
        <div className="font-mono text-[11px] text-cyan-300/80">
          {flip2_3 ? "2:3 form" : "3:2 form"}
        </div>
      </div>

      {/* Beat-position labels at top */}
      <div className="mb-1 grid grid-cols-[6.5rem_repeat(16,1fr)] items-end gap-1">
        <span />
        {labels.map((lab, i) => (
          <span key={i}
            className={`text-center text-[10px] font-mono ${
              lab === "1" || (i === 8 && lab === "1") ? "font-bold text-amber-300" : "text-cyan-300/50"
            }`}>
            {lab}
          </span>
        ))}
      </div>

      {/* Three rows */}
      <div className="space-y-2">
        {claves.map((c) => {
          const isActive = c.id === activeId;
          const cells = flip2_3 ? [...c.cells_3_2.slice(8), ...c.cells_3_2.slice(0, 8)] : c.cells_3_2;
          const col = colors[c.id];
          return (
            <button key={c.id} onClick={() => onPick(c.id)}
              className={`grid w-full grid-cols-[6.5rem_repeat(16,1fr)] items-center gap-1 rounded-xl border px-2 py-2 text-left transition-colors ${
                isActive ? `border-amber-400/60 bg-amber-500/5 ring-1 ${col.ring}` : "border-cyan-400/20 hover:bg-cyan-500/5"
              }`}>
              <span className={`text-[13px] font-bold ${isActive ? "text-amber-200" : "text-cyan-100"}`}>{c.name}</span>
              {cells.map((hit, i) => {
                const isStep = i === step;
                const isBar2Start = i === 8;
                return (
                  <motion.div key={i}
                    animate={{ scale: isStep ? 1.18 : 1, boxShadow: isStep ? "0 0 0 2px #FCD34D" : "0 0 0 0 transparent" }}
                    transition={{ duration: 0.1 }}
                    className={`relative flex h-12 items-center justify-center rounded ${
                      hit ? `${col.hit} ${isStep ? "ring-2 ring-amber-200" : ""}` : isStep ? "bg-zinc-700" : "bg-zinc-800/40"
                    } ${isBar2Start ? "ml-1" : ""}`}>
                    {hit && <span className="text-[11px] font-bold text-zinc-900">▾</span>}
                  </motion.div>
                );
              })}
            </button>
          );
        })}
      </div>

      {/* Bar markers */}
      <div className="mt-3 grid grid-cols-[6.5rem_1fr_1fr] gap-1 text-[10px] uppercase tracking-wider text-cyan-300/70">
        <span />
        <div className="rounded bg-zinc-800/40 py-0.5 text-center">Bar 1 · {flip2_3 ? "2-side" : "3-side"}</div>
        <div className="rounded bg-zinc-800/40 py-0.5 text-center">Bar 2 · {flip2_3 ? "3-side" : "2-side"}</div>
      </div>

      <p className="mt-2 text-[12px] italic text-cyan-300/70">
        Each cell is one 8th note. Stack the rows visually — the only difference between Son and
        Rumba shifts one eighth in bar 1; Bossa shifts one eighth in bar 2. Same nudge, different stroke.
      </p>
    </div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (<motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>{children}</motion.div>);
}
function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>{children}</blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
